import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Route as RouteIcon } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useCampusPicker } from '@/hooks/usePickers';
import { transportRoutesApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { RouteDirection, TransportRoute, TransportStatus } from '@/types/transport';
import { orDash } from '@/utils/format';
import { RouteStatusBadge } from './shared';

/**
 * The route network.
 *
 * <p>Every capacity figure on this screen — occupied, effective, free, the
 * percentage — arrives computed from the server. The page shows the arithmetic;
 * it never performs it, because the enrolment check lives behind the same
 * numbers and §38 will not have the two disagree.
 *
 * <p>The listing is paged and the search is a server-side query over code and
 * name — a district with eighty routes does not ship them all to type in a
 * filter box (§44).
 */

/** Spelled out rather than humanised: "dropoff" is not a word a parent reads. */
const DIRECTION_LABEL: Record<RouteDirection, string> = {
  PICKUP: 'Pickup only',
  DROPOFF: 'Drop-off only',
  BOTH: 'Both ways',
};

export function RoutesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('code,asc');
  const manages = can(PERMISSIONS.TRANSPORT_ROUTE_MANAGE);

  const [status, setStatus] = useState<TransportStatus | ''>('');
  const [direction, setDirection] = useState<RouteDirection | ''>('');
  const [campusFilter, setCampusFilter] = useState('');
  const [adding, setAdding] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [campusId, setCampusId] = useState('');
  const [newDirection, setNewDirection] = useState<RouteDirection>('BOTH');
  const [startLocation, setStartLocation] = useState('');
  const [endLocation, setEndLocation] = useState('');
  const [duration, setDuration] = useState('');
  const [distance, setDistance] = useState('');
  const [capacity, setCapacity] = useState('');
  const [notes, setNotes] = useState('');

  const campuses = useCampusPicker();

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status }),
    ...(direction === '' ? {} : { direction }),
    ...(campusFilter === '' ? {} : { campusId: campusFilter }),
  };

  const routes = useQuery({
    queryKey: queryKeys.transportRoutes.list(params),
    queryFn: () => transportRoutesApi.list(params),
  });

  const create = useMutation({
    mutationFn: () =>
      transportRoutesApi.create({
        code: code.trim(),
        name: name.trim(),
        campusId: campusId === '' ? null : campusId,
        direction: newDirection,
        startLocation: startLocation.trim() === '' ? undefined : startLocation.trim(),
        endLocation: endLocation.trim() === '' ? undefined : endLocation.trim(),
        estimatedDurationMinutes: duration === '' ? undefined : Number(duration),
        distanceKm: distance === '' ? undefined : Number(distance),
        capacity: Number(capacity),
        notes: notes.trim() === '' ? undefined : notes.trim(),
      }),
    onSuccess: (route) => {
      toast.success(`${route.displayName} created`);
      setAdding(false);
      setCode('');
      setName('');
      setCampusId('');
      setNewDirection('BOTH');
      setStartLocation('');
      setEndLocation('');
      setDuration('');
      setDistance('');
      setCapacity('');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.transportRoutes.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<TransportRoute>[] = [
    {
      key: 'route',
      header: 'Route',
      sortKey: 'code',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.transportRouteDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.name}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'direction',
      header: 'Direction',
      sortKey: 'direction',
      render: (row) => <span className="text-sm text-ink">{DIRECTION_LABEL[row.direction]}</span>,
    },
    { key: 'campus', header: 'Campus', render: (row) => orDash(row.campusName) },
    {
      key: 'staffing',
      header: 'Serves',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.vehicleName)}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.driverName)}</span>
        </span>
      ),
    },
    {
      key: 'occupancy',
      header: 'Occupancy',
      sortKey: 'capacity',
      className: 'text-right',
      // Occupied of effective — the server's min(budget, seats) arithmetic,
      // rendered rather than recomputed.
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold tabular-nums text-ink">
            {row.assignedStudents} of {row.effectiveCapacity}
          </span>
          <span className="block text-[11px] tabular-nums text-subtle">
            {row.availableSeats === 0 ? 'full' : `${row.availableSeats} free`} ·{' '}
            {row.utilizationPercent}%
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <RouteStatusBadge status={row.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Routes"
        description="The network the buses run. Capacity arithmetic is the server's."
        icon={<RouteIcon className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => setAdding(true)}>Add a route</Button> : undefined}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Code or name…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Running' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
            <FilterSelect
              label="Direction"
              value={direction}
              onChange={(value) => {
                setDirection(value);
                table.setPage(0);
              }}
              options={(['PICKUP', 'DROPOFF', 'BOTH'] as const).map((value) => ({
                value,
                label: DIRECTION_LABEL[value],
              }))}
            />
            <FilterSelect
              label="Campus"
              value={campusFilter}
              onChange={(value) => {
                setCampusFilter(value);
                table.setPage(0);
              }}
              options={(campuses.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            />
          </>
        }
        pagination={routes.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={routes.data?.data}
          rowKey={(row) => row.id}
          loading={routes.isPending}
          error={routes.error}
          onRetry={() => void routes.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={table.searchInput === '' ? 'No routes yet' : 'Nothing matched'}
          emptyDescription={
            table.searchInput === ''
              ? 'Add a route, give it stops, then put a vehicle and driver on it.'
              : 'The search covers route codes and names.'
          }
        />
      </ListPage>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a route"
        description="Stops, vehicle and driver are put on it from the route's own page."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={
                code.trim() === '' || name.trim() === '' || capacity === '' || create.isPending
              }
            >
              Create it
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              hint="Short and stable — R-01."
            />
            <TextField
              label="Seat budget"
              type="number"
              min={1}
              max={500}
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
              required
              hint="The working limit is the smaller of this and the vehicle's seats."
            />
          </div>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Campus
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={campusId}
              onChange={(event) => setCampusId(event.target.value)}
            >
              <option value="">Any campus</option>
              {(campuses.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Direction
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={newDirection}
              onChange={(event) => setNewDirection(event.target.value as RouteDirection)}
            >
              {(['BOTH', 'PICKUP', 'DROPOFF'] as const).map((value) => (
                <option key={value} value={value}>
                  {DIRECTION_LABEL[value]}
                </option>
              ))}
            </select>
            <span className="mt-1.5 block text-xs text-subtle">
              A both-ways route runs the same shape morning and afternoon. Different shapes are two
              routes.
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Starts at"
              value={startLocation}
              onChange={(event) => setStartLocation(event.target.value)}
            />
            <TextField
              label="Ends at"
              value={endLocation}
              onChange={(event) => setEndLocation(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Duration (min)"
              type="number"
              min={1}
              max={600}
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
            <TextField
              label="Distance (km)"
              type="number"
              min={0.1}
              step={0.1}
              value={distance}
              onChange={(event) => setDistance(event.target.value)}
            />
          </div>
          <TextField
            label="Notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
